import { sendEmailCode, signOut, verifyEmailCode } from "../lib/auth";
import { t } from "../lib/i18n";
import { h, mount } from "../ui/dom";
import { navigate } from "../ui/router";
import { signedIn } from "../ui/app";

/** Turn a supabase-js AuthError into something readable. */
function describe(error: unknown): string {
  if (error == null) return t("errGeneric");
  const e = error as Record<string, unknown>;
  const parts = [
    typeof e.message === "string" && e.message ? e.message : "",
    e.status ? `status=${String(e.status)}` : "",
  ].filter(Boolean);
  // AuthError props are non-enumerable, so JSON.stringify yields "{}" — hence
  // the explicit field list.
  return parts.join(" · ") || t("errGeneric");
}

export function renderSignIn(host: HTMLElement): void {
  if (signedIn) {
    mount(
      host,
      h("div", { class: "screen" },
        h("h2", { text: t("navSettings") }),
        h("button", {
          class: "danger",
          text: t("signOut"),
          on: {
            click: async () => {
              await signOut();
              navigate("/");
            },
          },
        }),
      ),
    );
    return;
  }

  const message = h("div", { class: "msg", hidden: true });
  const codeWrap = h("div", { class: "stack", hidden: true });

  const email = h("input", {
    type: "email",
    inputMode: "email",
    autocomplete: "email",
    placeholder: t("emailPlaceholder"),
    attrs: { autocapitalize: "off", spellcheck: "false" },
  });

  // autocomplete="one-time-code" is what lets iOS/Android offer the code from
  // the notification — worth more than it looks on a mobile-first app.
  const code = h("input", {
    type: "text",
    inputMode: "numeric",
    autocomplete: "one-time-code",
    maxLength: 10,
    placeholder: t("codeLabel"),
  });

  const say = (text: string, bad = false) => {
    message.hidden = false;
    message.textContent = text;
    message.className = bad ? "msg bad" : "msg good";
  };

  const send = h("button", {
    class: "primary",
    text: t("sendCode"),
    on: {
      click: async () => {
        if (!email.value.trim()) return say(t("emailLabel"), true);
        send.disabled = true;
        say(t("loading"));
        const { error } = await sendEmailCode(email.value);
        send.disabled = false;
        if (error) return say(describe(error), true);
        codeWrap.hidden = false;
        code.focus();
        say(t("codeSent"));
      },
    },
  });

  const verify = h("button", {
    class: "primary",
    text: t("verifyCode"),
    on: {
      click: async () => {
        verify.disabled = true;
        say(t("loading"));
        const { error } = await verifyEmailCode(email.value, code.value);
        verify.disabled = false;
        if (error) return say(describe(error), true);
        navigate("/");
      },
    },
  });

  mount(codeWrap, code, verify);

  mount(
    host,
    h("div", { class: "screen stack" },
      h("h2", { text: t("signIn") }),
      h("p", { class: "muted", text: t("signInPitch") }),
      email,
      send,
      codeWrap,
      message,
    ),
  );
}
